import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  confirmSignIn,
  fetchAuthSession,
  getCurrentUser,
  signIn,
  signOut,
  updatePassword,
  type AuthUser,
} from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Gruppe } from '@uep/shared';

/**
 * Anmeldezustand der App (PHASENPLAN 0.7).
 *
 * Die Rollenprüfung im UI ist Bedienkomfort, keine Sicherheitsgrenze — die
 * eigentliche Autorisierung passiert in AppSync über Cognito-Gruppen.
 */

export type AnmeldeSchritt = 'angemeldet' | 'abgemeldet' | 'neuesPasswort' | 'mfaCode' | 'laedt';

export interface AngemeldeterNutzer {
  benutzername: string;
  /**
   * Cognito-`sub` — die einzige Kennung, die sich nie ändert. Sie ist der
   * Schlüssel des persönlichen Profils; der Benutzername ist bei Cognito die
   * E-Mail-Adresse und kann wechseln.
   */
  sub: string;
  email: string;
  vorname: string;
  nachname: string;
  gruppen: Gruppe[];
}

interface AuthKontextWert {
  schritt: AnmeldeSchritt;
  nutzer: AngemeldeterNutzer | null;
  fehler: string | null;
  anmelden: (email: string, passwort: string) => Promise<void>;
  passwortSetzen: (neuesPasswort: string) => Promise<void>;
  mfaBestaetigen: (code: string) => Promise<void>;
  abmelden: () => Promise<void>;
  passwortAendern: (alt: string, neu: string) => Promise<void>;
  istAdmin: boolean;
  hatRolle: (rolle: Gruppe) => boolean;
  neuLaden: () => Promise<void>;
}

const AuthKontext = createContext<AuthKontextWert | null>(null);

const fehlerText = (fehler: unknown): string => {
  const name = fehler instanceof Error ? fehler.name : '';
  const meldungen: Record<string, string> = {
    NotAuthorizedException: 'E-Mail-Adresse oder Passwort ist falsch.',
    UserNotFoundException: 'E-Mail-Adresse oder Passwort ist falsch.',
    UserNotConfirmedException:
      'Der Zugang ist noch nicht bestätigt. Bitte prüfen Sie Ihre E-Mails oder wenden Sie sich an die Verwaltung.',
    PasswordResetRequiredException:
      'Für diesen Zugang wurde ein neues Passwort angefordert. Bitte prüfen Sie Ihre E-Mails.',
    TooManyRequestsException: 'Zu viele Versuche. Bitte warten Sie einen Moment.',
    LimitExceededException: 'Zu viele Versuche. Bitte warten Sie einen Moment.',
    InvalidPasswordException:
      'Das Passwort erfüllt die Vorgaben nicht: mindestens 12 Zeichen mit Groß- und Kleinbuchstaben sowie einer Ziffer.',
    CodeMismatchException: 'Der Code stimmt nicht. Bitte prüfen Sie die Eingabe.',
    ExpiredCodeException: 'Der Code ist abgelaufen. Bitte fordern Sie einen neuen an.',
    NetworkError: 'Keine Verbindung zum Server. Die Anmeldung braucht einmalig Netz.',
    UserAlreadyAuthenticatedException: 'Sie sind bereits angemeldet.',
  };
  if (meldungen[name]) return meldungen[name];
  return fehler instanceof Error && fehler.message
    ? fehler.message
    : 'Die Anmeldung ist fehlgeschlagen. Bitte versuchen Sie es erneut.';
};

const nutzerAusSession = async (roh: AuthUser): Promise<AngemeldeterNutzer> => {
  const session = await fetchAuthSession();
  const claims = session.tokens?.idToken?.payload ?? {};
  const gruppenRoh = claims['cognito:groups'];
  const gruppen = Array.isArray(gruppenRoh) ? (gruppenRoh as Gruppe[]) : [];

  return {
    benutzername: roh.username,
    sub: String(claims.sub ?? roh.userId ?? ''),
    email: String(claims.email ?? roh.signInDetails?.loginId ?? ''),
    vorname: String(claims.given_name ?? ''),
    nachname: String(claims.family_name ?? ''),
    gruppen,
  };
};

/** Liest die aktuelle Cognito-Sitzung. `null` heißt: niemand angemeldet. */
const ladeSitzung = async (): Promise<AngemeldeterNutzer | null> => {
  try {
    const roh = await getCurrentUser();
    return await nutzerAusSession(roh);
  } catch {
    return null;
  }
};

export const AuthAnbieter = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  /**
   * Die Sitzung ist Serverzustand und läuft deshalb über TanStack Query
   * (CLAUDE.md §2). Der Effekt unten abonniert nur noch die Amplify-Ereignisse
   * und setzt selbst keinen Zustand.
   */
  const sitzung = useQuery({
    queryKey: ['sitzung'],
    queryFn: ladeSitzung,
    retry: false,
    staleTime: 60_000,
  });

  /** Zwischenschritte der Anmeldung — reine UI-Zustände. */
  const [zwischenschritt, setZwischenschritt] = useState<
    'abgemeldet' | 'neuesPasswort' | 'mfaCode'
  >('abgemeldet');
  const [fehler, setFehler] = useState<string | null>(null);

  const nutzer = sitzung.data ?? null;
  const schritt: AnmeldeSchritt = sitzung.isPending
    ? 'laedt'
    : nutzer
      ? 'angemeldet'
      : zwischenschritt;

  const neuLaden = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['sitzung'] });
    await sitzung.refetch();
  }, [queryClient, sitzung]);

  useEffect(() => {
    const abbestellen = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedIn' || payload.event === 'tokenRefresh') {
        void queryClient.invalidateQueries({ queryKey: ['sitzung'] });
      }
      if (payload.event === 'signedOut') {
        queryClient.setQueryData(['sitzung'], null);
        setZwischenschritt('abgemeldet');
      }
    });
    return abbestellen;
  }, [queryClient]);

  const anmelden = useCallback(
    async (email: string, passwort: string) => {
      setFehler(null);
      try {
        const ergebnis = await signIn({ username: email.trim().toLowerCase(), password: passwort });
        const naechster = ergebnis.nextStep.signInStep;

        if (naechster === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
          setZwischenschritt('neuesPasswort');
          return;
        }
        if (
          naechster === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE' ||
          naechster === 'CONFIRM_SIGN_IN_WITH_SMS_CODE'
        ) {
          setZwischenschritt('mfaCode');
          return;
        }
        if (ergebnis.isSignedIn) {
          await neuLaden();
          return;
        }
        setFehler('Die Anmeldung verlangt einen Schritt, den diese App nicht unterstützt.');
      } catch (ausnahme) {
        setFehler(fehlerText(ausnahme));
      }
    },
    [neuLaden],
  );

  const passwortSetzen = useCallback(
    async (neuesPasswort: string) => {
      setFehler(null);
      try {
        const ergebnis = await confirmSignIn({ challengeResponse: neuesPasswort });
        if (ergebnis.isSignedIn) {
          await neuLaden();
          return;
        }
        if (ergebnis.nextStep.signInStep.startsWith('CONFIRM_SIGN_IN_WITH')) {
          setZwischenschritt('mfaCode');
        }
      } catch (ausnahme) {
        setFehler(fehlerText(ausnahme));
      }
    },
    [neuLaden],
  );

  const mfaBestaetigen = useCallback(
    async (code: string) => {
      setFehler(null);
      try {
        const ergebnis = await confirmSignIn({ challengeResponse: code.trim() });
        if (ergebnis.isSignedIn) await neuLaden();
      } catch (ausnahme) {
        setFehler(fehlerText(ausnahme));
      }
    },
    [neuLaden],
  );

  const abmelden = useCallback(async () => {
    await signOut();
    queryClient.setQueryData(['sitzung'], null);
    setZwischenschritt('abgemeldet');
  }, [queryClient]);

  const passwortAendern = useCallback(async (alt: string, neu: string) => {
    setFehler(null);
    try {
      await updatePassword({ oldPassword: alt, newPassword: neu });
    } catch (ausnahme) {
      setFehler(fehlerText(ausnahme));
      throw ausnahme;
    }
  }, []);

  const wert = useMemo<AuthKontextWert>(
    () => ({
      schritt,
      nutzer,
      fehler,
      anmelden,
      passwortSetzen,
      mfaBestaetigen,
      abmelden,
      passwortAendern,
      istAdmin: nutzer?.gruppen.includes('Admin') ?? false,
      hatRolle: (rolle: Gruppe) => nutzer?.gruppen.includes(rolle) ?? false,
      neuLaden,
    }),
    [
      schritt,
      nutzer,
      fehler,
      anmelden,
      passwortSetzen,
      mfaBestaetigen,
      abmelden,
      passwortAendern,
      neuLaden,
    ],
  );

  return <AuthKontext.Provider value={wert}>{children}</AuthKontext.Provider>;
};

export const useAuth = (): AuthKontextWert => {
  const kontext = useContext(AuthKontext);
  if (!kontext) {
    throw new Error('useAuth muss innerhalb von <AuthAnbieter> verwendet werden.');
  }
  return kontext;
};

export const anmeldeFehlerText = fehlerText;
